import { PageDataTypeCMSStaffPage } from "../../types";
import CMS from ".";
import { RouteComponentProps } from "../../declaration";
import { useState } from "preact/hooks";
import { lazy } from "preact-iso";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";

type Person = PageDataTypeCMSStaffPage["CMSStaffPage"]["staff"][0];
interface PersonEditorProps {
  person: Person;
  cancel: () => void;
  deletePerson: () => void;
  save: (person: Person) => void;
}
const Input = lazy(() => import("@mui/material/Input"));
const TextField = lazy(() => import("@mui/material/TextField"));
const Switch = lazy(() => import("@mui/material/Switch"));
const Button = lazy(() => import("@mui/material/Button"));

const PersonEditor = (props: PersonEditorProps) => {
  const [name, setName] = useState(props.person.name);
  const [hidden, setHidden] = useState(props.person.hidden);
  const [photo, setPhoto] = useState(props.person.photo);
  const [blurb, setBlurb] = useState(props.person.blurb);

  return (
    <>
      <table>
        <tbody>
          <tr>
            <td>Name:</td>
            <td>
              <Input
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
            </td>
          </tr>
          <tr>
            <td>Hidden:</td>
            <td>
              <Switch
                checked={hidden}
                onChange={(e) => setHidden(e.currentTarget.checked)}
              />
            </td>
          </tr>
          <tr>
            <td>Photo:</td>
            <td>
              <Input
                value={photo}
                onChange={(e) => setPhoto(e.currentTarget.value)}
              />
            </td>
          </tr>
          <tr>
            <td>Description:</td>
            <td>
              <TextField
                value={blurb}
                onChange={(e) => setBlurb(e.currentTarget.value)}
                multiline={true}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <Button onClick={() => props.save({ name, hidden, photo, blurb })}>
        Save Changes
      </Button>
      <Button onClick={() => props.deletePerson()}>Delete Person</Button>
      <Button onClick={() => props.cancel()}>Cancel Changes</Button>
    </>
  );
};

export default function CMSStaffPage(props: RouteComponentProps) {
  const pageData = props.innerHTML as PageDataTypeCMSStaffPage;
  const initialStaff = pageData.CMSStaffPage?.staff;
  const [staff, setStaff] = useState<Person[]>(initialStaff);

  const innerHTML = pageData.body;

  const [editing, setEditing] = useState<
    undefined | { idx: number; person: Person }
  >(undefined);

  const subProps = { ...props, innerHTML } as unknown as RouteComponentProps;

  const isEditor =
    props.currentUser?.claims.admin || props.currentUser?.claims.editor;

  const saveStaff = (newStaff: Person[]) => {
    const encodedPath = encodeURIComponent(props.path?.replace(/\?.*/, ""));
    const staff = newStaff.map((person) =>
      Object.fromEntries(
        Object.entries(person).filter(([_, value]) => value !== undefined)
      )
    ) as typeof newStaff;

    updateDoc(doc(db, "html-published/" + encodedPath), { staff }).then(() =>
      setStaff(staff)
    );
  };

  const moveUp = (idx: number) => {
    const newStaff = [
      ...staff.slice(0, Math.max(idx - 1, 0)),
      staff[idx],
      staff[idx - 1],
      ...staff.slice(idx + 1),
    ];
    saveStaff(newStaff);
  };

  const moveDown = (idx: number) => {
    const newStaff = [
      ...staff.slice(0, Math.max(idx, 0)),
      staff[idx + 1],
      staff[idx],
      ...staff.slice(idx + 2),
    ];
    saveStaff(newStaff);
  };

  const personList = (person: Person, idx: number, length: number) => {
    return (
      <tr>
        <td>
          {person.name} {person.hidden && " (hidden)"}
        </td>
        <td>
          {idx > 0 && <Button onClick={() => moveUp(idx)}>Move Up</Button>}
        </td>
        <td>
          {idx < length && (
            <Button onClick={() => moveDown(idx)}>Move Down</Button>
          )}
        </td>
        <td>
          <Button onClick={() => setEditing({ idx, person: staff[idx] })}>
            Edit
          </Button>
        </td>
      </tr>
    );
  };

  const pageEditor = () => {
    if (!isEditor) {
      return <></>;
    }
    const inner = () => {
      if (editing) {
        const cancel = () => setEditing(undefined);
        const save = (person: Person) => {
          const newStaff = [
            ...staff.slice(0, Math.max(editing.idx, 0)),
            person,
            ...staff.slice(editing.idx + 1),
          ];
          saveStaff(newStaff);
          setEditing(undefined);
        };
        const deletePerson = () => {
          if (confirm("Delete staff member's profile?")) {
            const newStaff = [
              ...staff.slice(0, Math.max(editing.idx, 0)),
              ...staff.slice(editing.idx + 1),
            ];
            saveStaff(newStaff);
            setEditing(undefined);
          }
        };

        return (
          <PersonEditor
            person={editing.person}
            cancel={cancel}
            save={save}
            deletePerson={deletePerson}
          />
        );
      }

      return (
        <>
          <table>
            <tbody>
              {staff.map((person, idx, arr) =>
                personList(person, idx, arr.length - 1)
              )}
            </tbody>
            <Button
              onClick={() =>
                setEditing({ idx: staff.length, person: { name: "new" } })
              }
            >
              Add New
            </Button>
          </table>
        </>
      );
    };

    return (
      <details class="staff-edit-form">
        <summary>Reorder/edit staff</summary>
        {inner()}
      </details>
    );
  };

  const renderPerson = (person: Person) => {
    return (
      <div class="person">
        {person.photo && (
          <div class="img-wrap">
            <img
              class="image_resized"
              src={person.photo}
              alt={person.name}
              title={person.name}
            />
          </div>
        )}
        {person.blurb || person.name}
      </div>
    );
  };

  return (
    <>
      {pageEditor()}
      <CMS {...subProps} />
      <div class="staff-list">
        {staff.filter((person) => !person.hidden).map(renderPerson)}
      </div>
    </>
  );
}
